import axios from 'axios';
import { Review } from '../type';
import { NetworkLoadingState, NetworkResponse, NetworkReviewsData } from '../types/NetworkTypes';

export const navigationFlag = {
    isNavigation: false,
};

/**
 * @param {string} userId
 * @returns {Promise<any>}
 */
export const getGoogleMyBusinessReviews = async (userId: string): Promise<any> => {
    try {
        const response = await axios.get(`/api/gmb/${userId}`, {
            timeout: 30000,
        });
        return response.data;
    } catch (error) {
        console.error('Error retrieving GMB reviews:', error);
        throw error;
    }
};

/**
 * @param {string} userId
 * @returns {Promise<any>}
 */
export const getFacebookReviews = async (userId: string): Promise<any> => {
    try {
        const response = await axios.get(`/api/facebook/${userId}`, {
            timeout: 30000,
        });
        return response.data;
    } catch (error) {
        console.error('Error retrieving Facebook reviews:', error);
        throw error;
    }
};

/**
 * @param {string} userId
 * @returns {Promise<any>}
 */
export const getOpinionSystemReviews = async (userId: string): Promise<any> => {
    try {
        const response = await axios.get(`/api/opinion-system/${userId}`, {
            timeout: 30000,
        });
        return response.data;
    } catch (error) {
        console.error('Error retrieving Opinion System reviews:', error);
        throw error;
    }
};

/**
 * @param {string} userId
 * @param {boolean} needUpdate
 * @param {function} onNetworkComplete
 * @returns {Promise<NetworkReviewsData>}
 */
export const getAllReviews = async (
    userId: string,
    needUpdate: boolean = false,
    onNetworkComplete?: (network: keyof NetworkLoadingState, data: NetworkResponse | null) => void,
): Promise<NetworkReviewsData> => {
    const responseData: NetworkReviewsData = {
        reviews: [],
        sources: {},
        loadingState: {
            gmb: true,
            facebook: true,
            opinionSystem: true,
        },
    };

    const updateNetworkData = (
        network: keyof NetworkLoadingState,
        data: NetworkResponse | null,
    ) => {
        if (data) {
            responseData.reviews = [...responseData.reviews, ...(data.reviews || [])];

            if (network === 'gmb') {
                responseData.sources.gmb = {
                    averageRating: data.averageRating || 0,
                    count: data.totalReviewCount || data.reviews?.length || 0,
                };
            } else if (network === 'facebook') {
                responseData.sources.facebook = {
                    averageRating: data.averageRating || 0,
                    count: data.reviews?.length || 0,
                };
            } else if (network === 'opinionSystem') {
                responseData.sources.opinion_system = {
                    averageRating: data.averageRating || 0,
                    count: data.totalReviewCount || data.reviews?.length || 0,
                };
            }
        }

        responseData.loadingState[network] = false;
        onNetworkComplete?.(network, data);
    };

    const gmbPromise = getGoogleMyBusinessReviews(userId)
        .then(data => {
            updateNetworkData('gmb', data);
        })
        .catch(error => {
            console.warn('[getAllReviews] Erreur GMB:', error.message);
            updateNetworkData('gmb', null);
        });

    const fbPromise = getFacebookReviews(userId)
        .then(data => {
            updateNetworkData('facebook', data);
        })
        .catch(error => {
            console.warn('[getAllReviews] Erreur Facebook:', error.message);
            updateNetworkData('facebook', null);
        });

    const osPromise = getOpinionSystemReviews(userId)
        .then(data => {
            updateNetworkData('opinionSystem', data);
        })
        .catch(error => {
            console.warn('[getAllReviews] Erreur Opinion System:', error.message);
            updateNetworkData('opinionSystem', null);
        });

    await Promise.all([gmbPromise, fbPromise, osPromise]);

    return responseData;
};

/**
 * @param {string} userId
 * @param {Record<string, string>} reviews
 * @returns {Promise<any>}
 */
export const updateReviewsStatus = async (userId: string, reviews: Record<string, string>) => {
    try {
        const response = await axios.put(`/api/reviews/status/${userId}`, {
            reviews,
        });

        return response.data;
    } catch (error) {
        console.error('Error updating review status:', error);
        throw error;
    }
};

/**
 * @param {Review[]} reviews
 * @returns {number}
 */
export const getCountUnreadReviewsFromData = (reviews: Review[]): number => {
    return reviews.filter(review => review.status === 'unread').length;
};

/**
 * @param {Review[]} reviews
 * @returns {Promise<any>}
 */
export const getCountUnreadReviewsByPlatforms = (reviews: Review[]): any => {
    let unreadCountFb = 0;
    let unreadCountGmb = 0;
    let unreadCountOS = 0;
    for (const review of reviews) {
        if (review.status === 'unread') {
            switch (review.network) {
                case 'facebook':
                    unreadCountFb = unreadCountFb + 1;
                    break;
                case 'google_my_business':
                    unreadCountGmb = unreadCountGmb + 1;
                    break;
                case 'opinion_system':
                    unreadCountOS = unreadCountOS + 1;
                    break;
            }
        }
    }
    return {
        unreadCountFb: unreadCountFb,
        unreadCountGmb: unreadCountGmb,
        unreadCountOS: unreadCountOS,
    };
};

/**
 * @param {string} userId
 * @returns {Promise<any>}
 */
export const checkFBPermissions = async (userId: string) => {
    try {
        const response = await axios.get(`/api/check-fb-permissions/${userId}`);
        return response.data?.isAllowed;
    } catch (error) {
        console.error('Error checking Facebook permissions:', error);
        throw error;
    }
};

export const getReviewsComments = async (userId: string): Promise<any> => {
    try {
        const response = await axios.get(`/api/reviews-comments/${userId}`, {
            timeout: 30000,
        });
        return response.data;
    } catch (error) {
        console.error('Error retrieving reviews comments:', error);
        throw error;
    }
};

export const syncReviewCountsAfterServerUpdate = async (userId: string): Promise<void> => {
    try {
        const { reviewsCacheService } = await import('./ReviewsCacheService');

        await reviewsCacheService.refreshCacheAfterCommentsCheck(userId);
    } catch (error) {
        console.warn(
            `[syncReviewCountsAfterServerUpdate] ⚠️ Erreur lors de la synchronisation pour ${userId}:`,
            error,
        );
    }
};
